/*
 * Merged article feed for the configured blogs on /blogs/dream-of-japan.
 *
 * Why client-side: neither blog.articles nor the /tagged/<tag> route spans more than
 * one blog, and paginate cannot be applied to an array built with concat. So the set
 * is assembled from the Storefront API in the browser.
 *
 * Loading happens in two stages, because waiting for the whole set before painting
 * anything cost about six times longer than painting the first page alone:
 *
 *   1. one small request for the articles the current page needs plus a buffer, rendered at once;
 *   2. the complete set for all configured blogs in the background.
 *
 * Articles with custom.hide_on_doj_blog set to true are removed before pagination.
 * The buffer normally absorbs those removals without another first-paint request;
 * cursor pagination is used as a fallback until the current page has enough visible
 * articles or the filtered source is exhausted.
 *
 * Once stage 2 lands, everything is in memory: page changes and section changes are
 * filtered and sliced locally, with no further requests. Until then the links behave
 * as ordinary links and the browser navigates, which is the pre-script behaviour.
 */
(function () {
  var configEl = document.getElementById('blog-feed-config');
  var container = document.querySelector('[data-blog-feed]');
  if (!configEl || !container) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }
  if (!config.token || config.token.indexOf('REPLACE_WITH') === 0) {
    container.innerHTML = '';
    return;
  }

  var pagination = document.querySelector('[data-blog-feed-pagination]');
  var pageNumbers = pagination ? pagination.querySelector('.page-numbers') : null;
  var header = document.querySelector('[data-blog-feed-header]');
  var headings = document.querySelectorAll('[data-blog-feed-heading]');
  var menuLinks = document.querySelectorAll('[data-blog-feed-link]');

  var RATIOS = { square: 1, landscape: 1.6, portrait: 0.8 };
  var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  var MAX_PER_REQUEST = 250;

  var activeTag = container.getAttribute('data-active-tag') || '';
  var imageRatio = container.getAttribute('data-image-ratio') || 'landscape';
  var showDate = container.getAttribute('data-show-date') === 'true';
  var showExcerpt = container.getAttribute('data-show-excerpt') === 'true';
  var pageSize = config.pageSize || 27;

  var allArticles = null;
  var viewArticles = [];
  var fullyLoaded = false;
  var renderedArticleKeys = '';
  var enabledBlogHandles = [];
  var blogTitles = [];

  /*
   * Section paths are read off the menu itself rather than hardcoded, so the mapping
   * cannot drift away from the links the theme actually renders.
   */
  var tagByPath = {};
  Array.prototype.forEach.call(menuLinks, function (link) {
    tagByPath[new URL(link.href, window.location.origin).pathname] = link.getAttribute('data-blog-feed-link');
  });

  var BLOGS_QUERY = [
    'query FeedBlogs($q: String!, $first: Int!) {',
    '  blogs(first: $first, query: $q) {',
    '    nodes { handle title }',
    '  }',
    '}'
  ].join('\n');

  /*
   * `content` is deliberately absent: it made Shopify strip HTML from every article
   * body and roughly doubled the response time, while only two articles in the whole
   * set have an empty excerpt to fall back on.
   */
  var QUERY = [
    'query MergedFeed($q: String!, $first: Int!, $cursor: String) {',
    '  articles(first: $first, after: $cursor, sortKey: PUBLISHED_AT, reverse: true, query: $q) {',
    '    pageInfo { hasNextPage endCursor }',
    '    nodes {',
    '      handle title publishedAt onlineStoreUrl tags',
    '      excerpt(truncateAt: 200)',
    '      blog { handle }',
    '      image {',
    '        altText width height',
    '        src320: url(transform: { maxWidth: 320 })',
    '        src640: url(transform: { maxWidth: 640 })',
    '      }',
    '      externalUrl: metafield(namespace: "custom", key: "external_url") { value }',
    '      hideOnDojBlog: metafield(namespace: "custom", key: "hide_on_doj_blog") { value }',
    '      downloadLink: metafield(namespace: "custom", key: "download_section_link") { value }',
    '      downloadTitle: metafield(namespace: "custom", key: "download_section_title") { value }',
    '      downloadButton: metafield(namespace: "custom", key: "download_section_button_text") { value }',
    '      downloadTriggerText: metafield(namespace: "custom", key: "download_section_trigger_text") { value }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  function quoteSearchValue(value) {
    return '"' + value.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';
  }

  function resolveBlogTitles() {
    return Promise.resolve().then(function () {
      if (!Array.isArray(config.blogs)) throw new Error('Blog feed requires a blogs configuration');

      config.blogs.forEach(function (source) {
        if (!source || typeof source.handle !== 'string' || !source.handle.trim() ||
            typeof source.enabled !== 'boolean') {
          throw new Error('Each blog requires a handle and a boolean enabled flag');
        }
        var handle = source.handle.trim();
        if (source.enabled && enabledBlogHandles.indexOf(handle) === -1) enabledBlogHandles.push(handle);
      });

      if (!enabledBlogHandles.length) throw new Error('No enabled blogs configured');
      if (enabledBlogHandles.length > MAX_PER_REQUEST) throw new Error('Too many enabled blogs configured');

      var filter = enabledBlogHandles.map(function (handle) {
        return 'handle:' + quoteSearchValue(handle);
      }).join(' OR ');

      return requestData(BLOGS_QUERY, { q: filter, first: enabledBlogHandles.length });
    }).then(function (data) {
      var blogsByHandle = Object.create(null);
      data.blogs.nodes.forEach(function (blog) {
        blogsByHandle[blog.handle] = blog;
      });
      var missing = enabledBlogHandles.filter(function (handle) {
        return !blogsByHandle[handle] || !blogsByHandle[handle].title;
      });
      if (missing.length) throw new Error('Enabled blogs not found: ' + missing.join(', '));

      blogTitles = enabledBlogHandles.map(function (handle) {
        return blogsByHandle[handle].title;
      });
    });
  }

  /*
   * The double quotes around the blog title are required. Without them Shopify treats
   * the words as separate terms and returns an almost empty result.
   */
  function buildFilter(tag) {
    var blogs = blogTitles
      .map(function (title) {
        return 'blog_title:' + quoteSearchValue(title);
      })
      .join(' OR ');
    var filter = '(' + blogs + ')';
    if (tag) filter += ' AND tag:' + quoteSearchValue(tag);
    return filter;
  }

  function request(filter, first, cursor) {
    return requestData(QUERY, { q: filter, first: first, cursor: cursor || null })
      .then(function (data) {
        return data.articles;
      });
  }

  function requestData(query, variables) {
    return fetch(config.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.token
      },
      body: JSON.stringify({
        query: query,
        variables: variables
      })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Storefront API ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        if (payload.errors && payload.errors.length) throw new Error(payload.errors[0].message);
        if (!payload.data) throw new Error('Storefront API returned no data');
        return payload.data;
      });
  }

  function fetchAll(filter, seed) {
    var collected = seed ? seed.nodes.slice() : [];
    function step(cursor) {
      return request(filter, MAX_PER_REQUEST, cursor).then(function (page) {
        collected = collected.concat(page.nodes);
        if (page.pageInfo.hasNextPage) return step(page.pageInfo.endCursor);
        return collected;
      });
    }
    if (seed && !seed.hasNextPage) return Promise.resolve(collected);
    return step(seed ? seed.endCursor : null);
  }

  function isVisibleOnDoj(article) {
    if (!article.blog || enabledBlogHandles.indexOf(article.blog.handle) === -1) return false;
    return !(
      article.hideOnDojBlog &&
      String(article.hideOnDojBlog.value).toLowerCase() === 'true'
    );
  }

  function visibleArticles(articles) {
    return articles.filter(isVisibleOnDoj);
  }

  /*
   * Fetch enough visible articles for the requested page. For the default page size
   * the first request asks for 40 rather than 27 nodes, so a handful of hidden posts
   * does not add a sequential round trip. More nodes are requested only when needed.
   */
  function fetchVisiblePrefix(filter, targetCount) {
    var rawNodes = [];
    var visibleNodes = [];
    var buffer = Math.max(10, Math.floor(pageSize / 2));

    function step(cursor) {
      var missing = Math.max(0, targetCount - visibleNodes.length);
      var count = rawNodes.length === 0
        ? targetCount + buffer
        : Math.max(pageSize, missing + buffer);
      count = Math.min(count, MAX_PER_REQUEST);

      return request(filter, count, cursor).then(function (page) {
        rawNodes = rawNodes.concat(page.nodes);
        visibleNodes = visibleNodes.concat(visibleArticles(page.nodes));

        if (visibleNodes.length < targetCount && page.pageInfo.hasNextPage) {
          return step(page.pageInfo.endCursor);
        }

        return {
          rawNodes: rawNodes,
          visibleNodes: visibleNodes,
          pageInfo: page.pageInfo
        };
      });
    }

    return step(null);
  }

  function filterByTag(articles, tag) {
    if (!tag) return articles;
    return articles.filter(function (article) {
      return (article.tags || []).indexOf(tag) !== -1;
    });
  }

  function articleUrl(article) {
    if (article.externalUrl && article.externalUrl.value) return article.externalUrl.value;
    if (article.onlineStoreUrl) return article.onlineStoreUrl;
    return '/blogs/' + article.blog.handle + '/' + article.handle;
  }

  function klaviyoListId(article) {
    var lists = config.klaviyoLists || {};
    var fallback = lists.default || '';
    var url = articleUrl(article).toLowerCase();
    var blogHandle = article.blog && article.blog.handle
      ? article.blog.handle.toLowerCase()
      : '';

    if (url.indexOf('japanesegreenteain.com') !== -1 ||
        url.indexOf('japanesegreentea.in') !== -1 ||
        url.indexOf('/blogs/green-tea-and-health/') !== -1 ||
        blogHandle === 'green-tea-and-health') return lists.tea || fallback;
    if (url.indexOf('japanesecoffeeco.com') !== -1 ||
        url.indexOf('/blogs/japanese-coffee-blog/') !== -1 ||
        blogHandle === 'japanese-coffee-blog') return lists.coffee || fallback;
    if (url.indexOf('japanesewineco.com') !== -1 ||
        url.indexOf('/blogs/japanese-wine/') !== -1 ||
        blogHandle === 'japanese-wine') return lists.wine || fallback;
    if (url.indexOf('/blogs/japanese-knife-stories/') !== -1 ||
        blogHandle === 'japanese-knife-stories') return lists.knife || fallback;
    if (url.indexOf('/blogs/miki/') !== -1 || blogHandle === 'miki') {
      return lists.mikipon || fallback;
    }
    return fallback;
  }

  function formatDate(value) {
    var date = new Date(value);
    if (isNaN(date.getTime())) return '';
    var day = date.getDate();
    return MONTHS[date.getMonth()] + ' ' + (day < 10 ? '0' + day : day) + ' ' + date.getFullYear();
  }

  function isRecent(value) {
    var date = new Date(value);
    if (isNaN(date.getTime())) return false;
    var twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    return date > twoYearsAgo;
  }

  function excerptText(article) {
    var text = (article.excerpt || '').trim();
    if (!text) return '';
    var words = text.split(/\s+/);
    if (words.length <= 30) return text;
    return words.slice(0, 30).join(' ') + '...';
  }

  function element(tag, className) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  }

  function link(href, title, className) {
    var node = element('a', className);
    node.setAttribute('href', href);
    node.setAttribute('title', title);
    return node;
  }

  function buildCard(article) {
    var column = element('div', 'small-12 medium-6 large-4 columns');
    var post = element('div', 'blog-post');
    var url = articleUrl(article);
    var title = article.title || '';

    var ratio = RATIOS[imageRatio];
    if (imageRatio === 'adapt' && article.image && article.image.width && article.image.height) {
      ratio = article.image.width / article.image.height;
    }
    if (!ratio) ratio = 1;

    var figure = element('figure', 'featured-image aspect-ratio');
    figure.style.setProperty('--ratio-percent', 100 / ratio + '%');
    var figureLink = link(url, title);
    if (article.image) {
      var img = element('img');
      img.setAttribute('width', article.image.width || '');
      img.setAttribute('height', article.image.height || '');
      img.setAttribute('src', article.image.src320);
      img.setAttribute('srcset', article.image.src320 + ' 320w, ' + article.image.src640 + ' 640w');
      img.setAttribute('sizes', '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw');
      img.setAttribute('alt', article.image.altText || title);
      img.setAttribute('loading', 'lazy');
      figureLink.appendChild(img);
    }
    figure.appendChild(figureLink);
    post.appendChild(figure);

    var info = element('div', 'blog-post-info-wrapper');

    if (showDate && article.publishedAt) {
      var meta = element('aside', 'post-meta');
      meta.textContent = formatDate(article.publishedAt);
      info.appendChild(meta);
    }

    var heading = element('h5');
    var headingLink = link(url, title);
    headingLink.textContent = title;
    heading.appendChild(headingLink);
    info.appendChild(heading);

    if (showExcerpt) {
      var text = excerptText(article);
      if (text) {
        var excerpt = element('div', 'post-excerpt');
        var paragraph = element('p');
        paragraph.textContent = text;
        excerpt.appendChild(paragraph);
        info.appendChild(excerpt);
      }
    }

    var footer = element('div', 'blog-post-footer');
    var readMoreWrap = element('div', 'read-more');
    var readMore = link(url, title, 'text-button blue');
    readMore.textContent = config.strings.readMore;
    readMoreWrap.appendChild(readMore);
    footer.appendChild(readMoreWrap);

    if (article.publishedAt && isRecent(article.publishedAt)) {
      var dateBlock = element('div', 'blog-post-date');
      var dateTitle = element('span', 'blog-post-date-title');
      dateTitle.textContent = config.strings.lastUpdated;
      var dateValue = element('span', 'blog-post-date-date');
      dateValue.textContent = formatDate(article.publishedAt);
      dateBlock.appendChild(dateTitle);
      dateBlock.appendChild(dateValue);
      footer.appendChild(dateBlock);
    }

    if (article.downloadLink && article.downloadLink.value) {
      var opener = document.createElement('div');
      opener.className = 'product-popup-modal__opener';
      var button = element('button', 'button outline small btn-download-recipe');
      button.setAttribute('type', 'button');
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('data-recipe-download', '');
      button.setAttribute('data-title', (article.downloadTitle && article.downloadTitle.value) || '');
      button.setAttribute('data-url', article.downloadLink.value);
      button.setAttribute('data-btn', (article.downloadButton && article.downloadButton.value) || '');
      button.setAttribute('data-klaviyo-list-id', klaviyoListId(article));
      var buttonLabel = element('span');
      buttonLabel.textContent =
        (article.downloadTriggerText && article.downloadTriggerText.value) ||
        config.strings.downloadRecipe;
      button.appendChild(buttonLabel);
      opener.appendChild(button);
      footer.appendChild(opener);
    }

    info.appendChild(footer);

    var tags = (article.tags || []).filter(function (tag) {
      return tag !== 'Amazon';
    });
    if (tags.length) {
      var tagWrap = element('div', 'blog-post-tags');
      tags.forEach(function (tag) {
        var tagLink = element('a', 'tag-link');
        tagLink.setAttribute(
          'href',
          '/blogs/' + article.blog.handle + '/tagged/' +
            tag.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
        );
        tagLink.textContent = tag;
        tagWrap.appendChild(tagLink);
      });
      info.appendChild(tagWrap);
    }

    post.appendChild(info);
    column.appendChild(post);
    return column;
  }

  function articleKey(article) {
    return article.blog.handle + '/' + article.handle;
  }

  function articleKeys(articles) {
    return articles.map(articleKey).join('\n');
  }

  function currentPage() {
    var value = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
    return value > 0 ? value : 1;
  }

  function pageHref(page) {
    var params = new URLSearchParams(window.location.search);
    if (page <= 1) {
      params.delete('page');
    } else {
      params.set('page', String(page));
    }
    var query = params.toString();
    return window.location.pathname + (query ? '?' + query : '');
  }

  var ARROW_PREV =
    '<svg width="24" height="14" viewBox="0 0 24 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M23.7065 7.26733H2.88477" stroke="currentColor" stroke-width="1.56" stroke-miterlimit="10"/>' +
    '<path d="M11.1264 0.940552L12.8323 2.07917L3.84042 7.26728L12.8323 12.4554L11.1264 13.5841L0.189453 7.26728L11.1264 0.940552Z" fill="currentColor"/>' +
    '</svg>';
  var ARROW_NEXT = ARROW_PREV.replace('<svg ', '<svg style="transform: scaleX(-100%)" ');

  function buildPagination(totalPages, page) {
    if (!pageNumbers) return;
    pageNumbers.innerHTML = '';

    /* Page count is unknown until the full set lands, so no controls are shown yet. */
    if (!fullyLoaded || totalPages <= 1) {
      if (pagination) pagination.style.display = 'none';
      return;
    }
    if (pagination) pagination.style.display = '';

    function addSpan(className, content, href) {
      var span = element('span', className);
      if (href) {
        var anchor = document.createElement('a');
        anchor.setAttribute('href', href);
        if (typeof content === 'string' && content.indexOf('<svg') === 0) {
          anchor.innerHTML = content;
        } else {
          anchor.textContent = content;
        }
        span.appendChild(anchor);
      } else if (typeof content === 'string' && content.indexOf('<svg') === 0) {
        span.innerHTML = content;
      } else {
        span.textContent = content;
      }
      pageNumbers.appendChild(span);
    }

    if (page > 1) addSpan('prev', ARROW_PREV, pageHref(page - 1));

    var shown = [];
    for (var i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - page) <= 2) shown.push(i);
    }

    var previous = 0;
    shown.forEach(function (number) {
      if (previous && number - previous > 1) addSpan('deco', '…');
      if (number === page) {
        addSpan('page current', String(number));
      } else {
        addSpan('page', String(number), pageHref(number));
      }
      previous = number;
    });

    if (page < totalPages) addSpan('next', ARROW_NEXT, pageHref(page + 1));
  }

  /* The theme hides the intro block past page one; keep that true across client-side moves. */
  function syncHeader(page) {
    if (header) header.classList.toggle('hide-info', page > 1);
    Array.prototype.forEach.call(headings, function (node) {
      node.hidden = node.getAttribute('data-blog-feed-heading') !== activeTag;
    });
    Array.prototype.forEach.call(menuLinks, function (node) {
      node.classList.toggle('active', node.getAttribute('data-blog-feed-link') === activeTag);
    });
  }

  function render(renderCards) {
    var page = currentPage();
    var totalPages = Math.max(1, Math.ceil(viewArticles.length / pageSize));
    if (fullyLoaded && page > totalPages) page = totalPages;

    if (renderCards) {
      var slice = viewArticles.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);
      var fragment = document.createDocumentFragment();
      slice.forEach(function (article) {
        fragment.appendChild(buildCard(article));
      });
      container.innerHTML = '';
      container.appendChild(fragment);
      renderedArticleKeys = articleKeys(slice);
    }

    buildPagination(totalPages, page);
    syncHeader(page);

    container.setAttribute('data-total', String(viewArticles.length));
    container.setAttribute('data-page', String(page));
    container.setAttribute('data-total-pages', String(fullyLoaded ? totalPages : 0));
    container.setAttribute('data-ready', fullyLoaded ? 'full' : 'partial');
  }

  function scrollTo(node) {
    if (!node) return;
    var top = node.getBoundingClientRect().top + window.pageYOffset - 120;
    window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
  }

  function isPlainClick(event) {
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  }

  if (pagination) {
    pagination.addEventListener('click', function (event) {
      if (!fullyLoaded || !isPlainClick(event)) return;
      var anchor = event.target.closest ? event.target.closest('a') : null;
      if (!anchor || !pagination.contains(anchor)) return;
      event.preventDefault();
      window.history.pushState({ blogFeed: true }, '', anchor.getAttribute('href'));
      render(true);
      scrollTo(container);
    });
  }

  Array.prototype.forEach.call(menuLinks, function (node) {
    node.addEventListener('click', function (event) {
      /* Before the full set is in memory there is nothing to filter, so let the browser navigate. */
      if (!fullyLoaded || !isPlainClick(event)) return;
      event.preventDefault();
      activeTag = node.getAttribute('data-blog-feed-link') || '';
      window.history.pushState({ blogFeed: true }, '', node.getAttribute('href'));
      viewArticles = filterByTag(allArticles, activeTag);
      render(true);
      scrollTo(header || container);
    });
  });

  window.addEventListener('popstate', function () {
    if (!fullyLoaded) return;
    activeTag = tagByPath[window.location.pathname] || '';
    viewArticles = filterByTag(allArticles, activeTag);
    render(true);
  });

  function showError(error) {
    container.innerHTML = '';
    var message = element('p', 'blog-feed-error');
    message.textContent = config.strings.error;
    container.appendChild(message);
    if (window.console) window.console.error('[blog-merged-feed]', error);
  }

  function loadFeed() {
    /* Stage 1: enough visible nodes for the current page, with a buffer to avoid extra requests. */
    var needed = currentPage() * pageSize;
    var stageOne = null;
    var initialLoadFailed = false;

    return fetchVisiblePrefix(buildFilter(activeTag), needed)
      .then(function (result) {
        stageOne = result;
        viewArticles = result.visibleNodes;
        render(true);
      })
      .catch(function (error) {
        initialLoadFailed = true;
        showError(error);
      })
      .then(function () {
        /* Stage 2: everything, unfiltered, so sections and pages need no more requests. */
        var seed = null;
        if (!activeTag && stageOne) {
          seed = {
            nodes: stageOne.rawNodes,
            hasNextPage: stageOne.pageInfo.hasNextPage,
            endCursor: stageOne.pageInfo.endCursor
          };
        }

        return fetchAll(buildFilter(''), seed).then(function (articles) {
          allArticles = visibleArticles(articles);
          fullyLoaded = true;
          viewArticles = filterByTag(allArticles, activeTag);
          var page = currentPage();
          var totalPages = Math.max(1, Math.ceil(viewArticles.length / pageSize));
          if (page > totalPages) page = totalPages;
          var expected = viewArticles.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);
          render(initialLoadFailed || articleKeys(expected) !== renderedArticleKeys);
        });
      })
      .catch(function (error) {
        if (window.console) window.console.error('[blog-merged-feed] background load failed', error);
      });
  }

  resolveBlogTitles().then(loadFeed).catch(showError);
})();
